import { useEditorStore } from "@/store";
import { InfoBanner } from "@/components/layout/InfoBanner";
import { SectionHeader } from "@/components/fields/SectionHeader";
import { StringField } from "@/components/fields/StringField";
import { NumberField } from "@/components/fields/NumberField";
import { flyToInitialView } from "@/lib/mapCommands";

export function RootSection() {
  const style = useEditorStore((s) => s.style);
  const updateRoot = useEditorStore((s) => s.updateRoot);
  const viewport = useEditorStore((s) => s.viewport);

  const center = Array.isArray(style.center) ? (style.center as number[]) : [0, 0];

  /** Capture current map viewport as the initial view */
  const captureCurrentView = () => {
    updateRoot("center", [viewport.center[0], viewport.center[1]]);
    updateRoot("zoom", Math.round(viewport.zoom * 10) / 10);
    updateRoot("pitch", Math.round(viewport.pitch));
    updateRoot("bearing", Math.round(viewport.bearing));
  };

  return (
    <div>
      <InfoBanner storageKey="section-root">
        The initial view that loads when someone opens this style. These values
        are saved into the style.json and determine the default center, zoom, tilt,
        and rotation of the map. The purple area on the map shows the approximate
        initial viewport.
      </InfoBanner>

      <SectionHeader title="General" description="Basic style identification">
        <StringField
          label="Name"
          value={style.name as string | undefined}
          onChange={(v) => updateRoot("name", v)}
        />
      </SectionHeader>

      <SectionHeader title="Initial View" description="Camera position when the style first loads">
        <div className="mb-2 flex items-center justify-end">
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={flyToInitialView}
              className="rounded-radius-sm border border-border-muted px-2 py-0.5 text-[10px] font-medium text-text-secondary hover:bg-surface-sunken transition-colors"
              title="Fly the map to the configured initial view"
            >
              Fly to
            </button>
            <button
              type="button"
              onClick={captureCurrentView}
              className="rounded-radius-sm border border-accent bg-accent/10 px-2 py-0.5 text-[10px] font-medium text-accent hover:bg-accent hover:text-accent-text transition-colors"
              title="Set initial view to the current map position"
            >
              Capture current
            </button>
          </div>
        </div>

        <NumberField
          label="Longitude"
          value={center[0] as number}
          onChange={(v) => updateRoot("center", [v, (center[1] as number) ?? 0])}
          min={-180}
          max={180}
          step={0.01}
        />
        <NumberField
          label="Latitude"
          value={center[1] as number}
          onChange={(v) => updateRoot("center", [(center[0] as number) ?? 0, v])}
          min={-85}
          max={85}
          step={0.01}
        />
        <NumberField
          label="Zoom"
          value={style.zoom as number | undefined}
          onChange={(v) => updateRoot("zoom", v)}
          min={0}
          max={24}
          step={0.1}
        />
        <NumberField
          label="Pitch"
          value={style.pitch as number | undefined}
          onChange={(v) => updateRoot("pitch", v)}
          min={0}
          max={85}
          step={1}
        />
        <NumberField
          label="Bearing"
          value={style.bearing as number | undefined}
          onChange={(v) => updateRoot("bearing", v)}
          min={0}
          max={360}
          step={1}
        />
      </SectionHeader>
    </div>
  );
}
